import { useMemo, useRef, useState } from "react";
import { Alert, Button, DatePicker, Empty, Form, Input, Modal, Skeleton, Space, Tabs, Tooltip, Typography } from "antd";
import { EditOutlined, PlayCircleOutlined, StopOutlined, TeamOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getRtpRights, isPlanReadOnly, planDayProgress } from "@utils/rtp_utils";
import {
	rtpApi,
	runRtpWrite,
	useCurrentUserUuid,
	useRtpBlockLibrary,
	useRtpMetricProgress,
	useRtpPlan,
} from "./rtpHooks";
import { OverviewTab } from "./OverviewTab";
import { DayPlansTab } from "./DayPlansTab";
import { ProgressTab } from "./ProgressTab";
import { BlockLibraryTab } from "./BlockLibraryTab";
import { NotesTab } from "./NotesTab";
import { EventLogTab } from "./EventLogTab";
import { StaffModal } from "./StaffModal";
import "@styles/styles-RtpPlanner.css";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface PlanDetailProps {
	planId: number;
	onBack: () => void;
}

interface StartPlanFormValues {
	startDate: dayjs.Dayjs;
}

interface EditPlanFormValues {
	name: string;
	description?: string;
}

export const PlanDetail = ({ planId, onBack }: PlanDetailProps) => {
	const { plan, loading, error, refetch } = useRtpPlan(planId);
	const library = useRtpBlockLibrary();
	const currentUserUuid = useCurrentUserUuid();

	const [startOpen, setStartOpen] = useState(false);
	const [startSubmitting, setStartSubmitting] = useState(false);
	const [startForm] = Form.useForm<StartPlanFormValues>();

	const [editOpen, setEditOpen] = useState(false);
	const [editSubmitting, setEditSubmitting] = useState(false);
	const [editForm] = Form.useForm<EditPlanFormValues>();

	const [staffOpen, setStaffOpen] = useState(false);
	const [activeTab, setActiveTab] = useState("overview");
	// The library entry that the Block Library tab brings into view. The tab clears it when it has it.
	const [libraryFocusId, setLibraryFocusId] = useState<string | null>(null);
	const tabsRef = useRef<HTMLDivElement>(null);
	// Opens the Progress tab and scrolls that metric's card to the top of the view, in full. The
	// page keeps its scroll position across a tab change, so without this the card could be
	// anywhere. Falls back to the tab bar if the card is not there.
	const openProgressTab = (metricKey: string) => {
		setActiveTab("progress");
		requestAnimationFrame(() => {
			const card = tabsRef.current?.querySelector<HTMLElement>(`[data-metric-key="${CSS.escape(metricKey)}"]`);
			(card ?? tabsRef.current)?.scrollIntoView({ block: "start", behavior: "smooth" });
		});
	};

	// Opens the Block Library tab and brings the card of that library entry into view.
	const openLibraryEntry = (entryId: string) => {
		setLibraryFocusId(entryId);
		setActiveTab("block-library");
	};

	// Progress data is prefetched here, as soon as the plan arrives, so the Progress tab opens
	// with its charts already loaded (the tab itself still mounts lazily for correct chart widths).
	// A plan that has not started has no progress yet, and the backend rejects the request.
	const progressMetricKeys = useMemo(() => {
		const keys: string[] = [];
		if (!plan || plan.status === "draft" || !plan.startDate) return keys;
		for (const phase of plan.phases) {
			for (const criterion of phase.criteria) {
				const key = criterion.details.metricKey;
				if (criterion.criterionType === "metric" && key && !keys.includes(key)) keys.push(key);
			}
		}
		return keys;
	}, [plan]);
	const metricProgress = useRtpMetricProgress(plan?.id ?? 0, progressMetricKeys);

	if (loading && !plan) {
		return <Skeleton active />;
	}

	if (error || !plan) {
		return (
			<Empty description="Couldn't load this plan.">
				<Button data-attr="settings.rtp-plan.back" onClick={onBack}>
					All plans
				</Button>
			</Empty>
		);
	}

	const rights = getRtpRights(plan, currentUserUuid);
	const readOnly = isPlanReadOnly(plan);
	const canManage = rights.isManager && !readOnly;

	const dayProgress = planDayProgress(plan);

	const openStartModal = () => {
		startForm.setFieldsValue({ startDate: dayjs() });
		setStartOpen(true);
	};

	const closeStartModal = () => {
		setStartOpen(false);
		startForm.resetFields();
	};

	const handleStartSubmit = async () => {
		let values: StartPlanFormValues;
		try {
			values = await startForm.validateFields();
		} catch {
			return;
		}
		setStartSubmitting(true);
		const ok = await runRtpWrite(
			() =>
				rtpApi.rtpPlanAction(plan.id, {
					action: "START_PLAN",
					startDate: values.startDate.format("YYYY-MM-DD"),
				}),
			refetch
		);
		setStartSubmitting(false);
		if (ok) closeStartModal();
	};

	const openEditModal = () => {
		editForm.setFieldsValue({ name: plan.name, description: plan.description });
		setEditOpen(true);
	};

	const closeEditModal = () => {
		setEditOpen(false);
		editForm.resetFields();
	};

	const handleEditSubmit = async () => {
		let values: EditPlanFormValues;
		try {
			values = await editForm.validateFields();
		} catch {
			return;
		}
		setEditSubmitting(true);
		const ok = await runRtpWrite(
			() => rtpApi.patchRtpPlan(plan.id, { name: values.name, description: values.description }),
			refetch
		);
		setEditSubmitting(false);
		if (ok) closeEditModal();
	};

	const handleCancelPlan = () => {
		Modal.confirm({
			title: "Cancel this plan?",
			content: "The plan becomes read-only and the injury is freed for a new plan.",
			okText: "Cancel Plan",
			okType: "danger",
			okButtonProps: { "data-attr": "settings.rtp-plan.cancel-submit" } as any,
			onOk: () => runRtpWrite(() => rtpApi.rtpPlanAction(plan.id, { action: "CANCEL_PLAN" }), refetch),
		});
	};

	const tabItems = [
		{
			key: "overview",
			label: "Overview",
			children: (
				<OverviewTab
					plan={plan}
					refetch={refetch}
					progressByKey={metricProgress.byKey}
					onOpenProgress={openProgressTab}
				/>
			),
		},
		{
			key: "day-plans",
			label: "Schedule",
			children: (
				<DayPlansTab plan={plan} refetch={refetch} library={library} onOpenLibraryEntry={openLibraryEntry} />
			),
		},
		{
			key: "progress",
			label: "Progress",
			children: (
				<ProgressTab plan={plan} progressByKey={metricProgress.byKey} progressLoading={metricProgress.loading} />
			),
		},
		{
			key: "block-library",
			label: "Block Library",
			children: (
				<BlockLibraryTab
					plan={plan}
					library={library}
					focusId={libraryFocusId}
					onFocusHandled={() => setLibraryFocusId(null)}
				/>
			),
		},
		{ key: "notes", label: "Notes", children: <NotesTab plan={plan} /> },
		{ key: "event-log", label: "Event Log", children: <EventLogTab plan={plan} /> },
	];

	return (
		<div>
			<Button
				type="link"
				data-attr="settings.rtp-plan.back"
				onClick={onBack}
				style={{ paddingLeft: 0, marginBottom: 8 }}
			>
				← All plans
			</Button>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
				<div>
					<Space align="center">
						<Title level={3} style={{ marginBottom: 0 }}>
							{plan.name}
						</Title>
						<span className={`rtp-status-pill rtp-status-pill--${plan.status}`}>{plan.status}</span>
						{canManage && plan.status === "draft" ? (
							<Alert
								type="info"
								className="rtp-plan-not-started"
								message={
									<>
										Ready to go? Click the green <b>Start</b> button at the top right.
									</>
								}
							/>
						) : null}
					</Space>
					<div>
						<Text type="secondary">
							{plan.playerName}
							{dayProgress ? ` · Day ${dayProgress.dayNumber} of ${dayProgress.totalDays}` : ""}
						</Text>
					</div>
					{plan.description ? (
						<Paragraph
							className="rtp-pre-wrap"
							style={{ marginTop: 8 }}
							ellipsis={{
								rows: 2,
								expandable: "collapsible",
								symbol: (expanded: boolean) => (expanded ? "Show less" : "Show more"),
							}}
						>
							{plan.description}
						</Paragraph>
					) : null}
				</div>
				{canManage ? (
					<Space size={8}>
						{plan.status === "draft" ? (
							<Tooltip title="Start plan">
								<Button
									color="green"
									variant="solid"
									shape="circle"
									className="rtp-start-pulse"
									icon={<PlayCircleOutlined />}
									aria-label="Start plan"
									data-attr="settings.rtp-plan.start"
									onClick={openStartModal}
								/>
							</Tooltip>
						) : null}
						<Tooltip title="Edit plan">
							<Button
								color="primary"
								variant="solid"
								shape="circle"
								icon={<EditOutlined />}
								aria-label="Edit plan"
								data-attr="settings.rtp-plan.edit"
								onClick={openEditModal}
							/>
						</Tooltip>
						<Tooltip title="Manage staff">
							<Button
								color="primary"
								variant="solid"
								shape="circle"
								icon={<TeamOutlined />}
								aria-label="Manage staff"
								data-attr="settings.rtp-plan.manage-staff"
								onClick={() => setStaffOpen(true)}
							/>
						</Tooltip>
						<Tooltip title="Cancel plan">
							<Button
								color="danger"
								variant="solid"
								shape="circle"
								icon={<StopOutlined />}
								aria-label="Cancel plan"
								data-attr="settings.rtp-plan.cancel"
								onClick={handleCancelPlan}
							/>
						</Tooltip>
					</Space>
				) : null}
			</div>

			<div ref={tabsRef} className="rtp-plan-tabs">
				<Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} style={{ marginTop: 16 }} />
			</div>

			<Modal
				title="Start Plan"
				open={startOpen}
				onCancel={closeStartModal}
				destroyOnHidden
				onOk={handleStartSubmit}
				confirmLoading={startSubmitting}
				okButtonProps={{ "data-attr": "settings.rtp-plan.start-submit" } as any}
			>
				<Form form={startForm} layout="vertical">
					<Form.Item
						name="startDate"
						label="Start date"
						rules={[{ required: true, message: "Select a start date" }]}
					>
						<DatePicker style={{ width: "100%" }} />
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title="Edit Plan"
				open={editOpen}
				onCancel={closeEditModal}
				destroyOnHidden
				onOk={handleEditSubmit}
				confirmLoading={editSubmitting}
				okButtonProps={{ "data-attr": "settings.rtp-plan.edit-submit" } as any}
			>
				<Form form={editForm} layout="vertical">
					<Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: "Enter a name" }]}>
						<Input placeholder="Plan name" />
					</Form.Item>
					<Form.Item name="description" label="Description">
						<TextArea rows={3} placeholder="Optional description" />
					</Form.Item>
				</Form>
			</Modal>

			<StaffModal plan={plan} open={staffOpen} onClose={() => setStaffOpen(false)} refetch={refetch} />
		</div>
	);
};
